import { Activity, Scale, Target, BarChart3 } from "lucide-react";

import { Card } from "@/components/ui/card";

interface ValueProp {
  title: string;
  description: string;
  Icon: typeof Activity;
}

/**
 * Section "Pourquoi SunMatch ?" de la landing (S7-5, GAP-3 ; copy refondue 2026-06-17).
 *
 * 4 cartes resserrées autour des différenciateurs (l'intro vit désormais dans le
 * hero — plus de sous-paragraphe ici) :
 * - "Vos données réelles, pas une moyenne"  → match conso × production au pas 30 min.
 * - "Aucun intérêt à surdimensionner"        → indépendance, ne vend pas d'installation.
 * - "Le bon scénario est le vôtre"           → rentabilité/autoconso/autonomie, arbitrages.
 * - "Des chiffres honnêtes"                  → flux visualisés, ROI seulement sur 1 an de données.
 *
 * Responsive : 1 col mobile, 2 cols tablette, 4 cols desktop (S7-1 § 1.3).
 */
const PROPS: ValueProp[] = [
  {
    title: "Vos données réelles, pas une moyenne",
    description:
      "SunMatch confronte votre consommation réelle à la production de vos futurs panneaux (PVGIS), demi-heure par demi-heure. C'est cette confrontation, et non votre total annuel, qui révèle la bonne taille de panneaux et de batterie.",
    Icon: Activity,
  },
  {
    title: "Aucun intérêt à surdimensionner",
    description:
      "SunMatch ne vend ni panneaux ni installation. Le dimensionnement sert vos objectifs, pas la marge d'un devis : un repère neutre à consulter avant de vous engager.",
    Icon: Scale,
  },
  {
    title: "Le bon scénario est le vôtre",
    description:
      "Rentabilité, autoconsommation ou autonomie ne mènent pas à la même installation. Comparez les scénarios (panneaux, batterie ou non) et décidez selon vos priorités. Le retour sur investissement est un critère, pas forcément le verdict.",
    Icon: Target,
  },
  {
    title: "Des chiffres honnêtes",
    description:
      "Flux d'énergie visualisés, hypothèses affichées, et pour le retour sur investissement, on attend une année complète de données. Calculé sur quelques mois (un seul été, un seul hiver), le retour sur investissement serait trompeur.",
    Icon: BarChart3,
  },
];

export function LandingValueProps(): JSX.Element {
  return (
    <section
      aria-labelledby="value-props-title"
      className="bg-white pt-8 pb-16 sm:pt-10 sm:pb-20"
    >
      <div className="mx-auto max-w-6xl px-4">
        <div className="mx-auto max-w-2xl text-center">
          <h2
            id="value-props-title"
            className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl"
          >
            Pourquoi SunMatch ?
          </h2>
        </div>

        <ul
          aria-label="Liste des bénéfices SunMatch"
          className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-4"
        >
          {PROPS.map(({ title, description, Icon }) => (
            <li key={title}>
              <Card className="h-full space-y-3 p-6">
                <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
                  <Icon className="h-5 w-5" aria-hidden />
                </div>
                <h3 className="text-lg font-semibold text-slate-900">
                  {title}
                </h3>
                <p className="text-justify text-sm leading-relaxed text-slate-700">
                  {description}
                </p>
              </Card>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
